import React, { useEffect, useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Cell
} from "recharts";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

import { apiAxios } from "../../../utils/apiAxios";
import { MillingPerformanceSummary } from "../../../components/CentralHub/Admin/Interfaces/MillingPerformanceSummary";
import { MillingQueueItem } from "../../../components/CentralHub/Admin/Interfaces/MillingQueueItem";
import { DelayedOrder } from "../../../components/CentralHub/Admin/Interfaces/ManagerDelayed";

import "../../../style/millingperformence.css";

const ManagerMillingPerformance: React.FC = () => {

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // ---------------- STATES ----------------
  const [search, setSearch] = useState("");

  const [kpi, setKpi] = useState<MillingPerformanceSummary>({
    totalCases: 0,
    completedCases: 0,
    millingQueueCount: 0,
    needsAttention: 0
  });

  const [animatedKpi, setAnimatedKpi] = useState(kpi);

  const [designerQueue, setDesignerQueue] = useState<any[]>([]);
  const [millingQueue, setMillingQueue] = useState<MillingQueueItem[]>([]);
  const [deliveryQueue, setDeliveryQueue] = useState<any[]>([]);

  const [hubPerformance, setHubPerformance] = useState<any[]>([]);
  const [productionData, setProductionData] = useState<any[]>([]);
  const [delayedOrders, setDelayedOrders] = useState<DelayedOrder[]>([]);

  const [waitingJobs, setWaitingJobs] = useState<number>(0);
  // const [hubLoad, setHubLoad] = useState<any>({});
  const [hubLoad, setHubLoad] = useState<Record<string, number>>({});
  const [suggestion, setSuggestion] = useState<string>("");

  const [bestHub, setBestHub] = useState<string>("");
  const [worstHub, setWorstHub] = useState<string>("");
const [selectedHub, setSelectedHub] = useState<any>(null);
const [showModal, setShowModal] = useState(false);
const [loading, setLoading] = useState(false);
const [searchText, setSearchText] = useState("");
  // ---------------- ANIMATION ----------------
  useEffect(() => {
    let start = 0;
    const duration = 800;
    const step = 20;

    const interval = setInterval(() => {
      start += step;

      setAnimatedKpi({
        totalCases: Math.min(start, kpi.totalCases),
        completedCases: Math.min(start, kpi.completedCases),
        millingQueueCount: Math.min(start, kpi.millingQueueCount),
        needsAttention: Math.min(start, kpi.needsAttention)
      });

      if (start >= Math.max(
        kpi.totalCases,
        kpi.completedCases,
        kpi.millingQueueCount,
        kpi.needsAttention
      )) {
        clearInterval(interval);
      }

    }, duration / 20);

    return () => clearInterval(interval);
  }, [kpi]);
const getBarColor = (label: any) => {
  switch (label) {
    case "COMPLETED":
      return "#28a745"; // green
    case "READY_FOR_DELIVERY":
      return "#ffc107"; // yellow
    case "OUT_FOR_DELIVERY":
      return "#0d6efd"; // blue
    default:
      return "rgba(13, 110, 253, 0.3)"; // gray
  }
};
  // ---------------- AUTO REFRESH ----------------
  useEffect(() => {
    fetchMillingData();
    const interval = setInterval(fetchMillingData, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchMillingData = async () => {
    setLoading(true);
    try {
      const res = await apiAxios.get(`${API_BASE}/Central/milling-performance`);
      const data = res.data.singleData;

      setKpi({
        totalCases: data.summary.totalCases,
        completedCases: data.summary.completedCases,
        millingQueueCount: data.millingQueue.length,
        needsAttention: data.summary.needsAttention
      });

      setDesignerQueue(data.designerQueue);
      setMillingQueue(data.millingQueue);
      setDeliveryQueue(data.deliveryQueue);

      calculateHubLoad(data.millingQueue);

      // setHubPerformance(
      //   data.orderStates.map((x: any) => ({
      //     hub: x.currentOrderState,
      //     orders: x.totalOrders
      //   }))
      // );

         const allowedStates = [
  "PENDING_ACCEPTANCE",
  "OUT_FOR_DELIVERY",
  "READY_FOR_DELIVERY"
];

setHubPerformance(
  data.orderStates
    .filter((x: any) => allowedStates.includes(x.currentOrderState))
    .map((x: any) => ({
      hub: x.currentOrderState,
      orders: x.totalOrders
    }))
);

      setProductionData([
        { hour: "9 AM", orders: 3 },
        { hour: "10 AM", orders: 5 },
        { hour: "11 AM", orders: 4 },
        { hour: "12 PM", orders: 6 }
      ]);

      setDelayedOrders(
        data.delayedOrder
          //.filter((x: any) => x.currentOrderState === "DELAY_REPORT")
          .map((x: any) => ({
            caseID:x.caseID,
            orderID: x.orderID,
            status:x.currentOrderState,
            delayHours: x.delayDuration,
            orderTypeName:x.orderTypeName
          }))
      );
setLoading(false);
    } catch (error) {
      console.error("Dashboard API Error:", error);
    }
  };

   const calculateHubLoad = (queue: MillingQueueItem[]) => {
     setWaitingJobs(queue.length);
 
     const load: any = {};
     queue.forEach((item) => {
       load[item.millingHub] = (load[item.millingHub] || 0) + 1;
     });
 
     setHubLoad(load);
 const hubs = Object.entries(load);
 if (hubs.length === 0) return;
 
 const sorted: any = hubs.sort((a: any, b: any) => b[1] - a[1]);
 
 // ✅ ADD THIS CONDITION
 if (sorted.length === 1) {
   setBestHub(sorted[0][0]);
   setWorstHub("-"); // or "N/A"
 
   setSuggestion("Only one hub available");
 
   return;
 }
 
 // existing logic
 const overloadedHub = sorted[0];
 const freeHub = sorted[sorted.length - 1];
 
 setWorstHub(overloadedHub[0]);
 setBestHub(freeHub[0]);
 
 const difference = overloadedHub[1] - freeHub[1];
 
 setSuggestion(
   difference > 0
     ? `Move ${Math.ceil(difference / 2)} jobs from ${overloadedHub[0]} → ${freeHub[0]}`
     : "All hubs balanced"
 );
   };
 const renderDateWithTooltip = (dateValue: string) => {
  const date = new Date(dateValue);

  return (
    <span
      title={date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })}
      style={{ cursor: "pointer" }}
    >
      {date.toLocaleDateString("en-GB")}
    </span>
  );
};
  // ---------------- SEARCH FILTER ----------------
  const filterData = (data: any[]) =>
    data.filter((row) =>
      Object.values(row).join(" ").toLowerCase().includes(search.toLowerCase())
    );

  const queueColumns: TableColumn<any>[] = [
    { name: "Case ID", selector: row => row.caseID },
    { name: "Order ID", selector: row => row.orderID },
    {
    name: "Assigned On",
    cell: row => renderDateWithTooltip(row.assignedOn)
  }
  ];

  const millingColumns: TableColumn<MillingQueueItem>[] = [
    { name: "Case ID", selector: row => row.caseID },
    { name: "Order ID", selector: row => row.orderID },
    { name: "Milling Hub", selector: row => row.millingHub },
    {
    name: "Assigned On",
    cell: row => renderDateWithTooltip(row.assignedOn)
  }
  ];

 const delayOrderColumns: TableColumn<DelayedOrder>[] = [
  { name: "Order ID", selector: row => row.orderID },
  { name: "Type", selector: row => row.orderTypeName },
  { name: "Status", selector: row => row.status },

  {
    name: "Delay",
    minWidth: "170px",
    cell: (row: DelayedOrder) => {
      const isBreach = row.status === "SLA_BREACH";

      return (
        <span
          style={{
            padding: "6px 8px",
            borderRadius: "5px",
            fontSize: "13px",
            whiteSpace: "nowrap",
            fontWeight: 400,
            textAlign: "center",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: isBreach ? "#ffe5e5" : "transparent",
            color: isBreach ? "rgb(227, 93, 106)" : "#333",
          }}
          className={isBreach ? "blink-status" : ""}
        >
          {row.delayHours}
        </span>
      );
    }
  }
];

    const filteredOrders = useMemo(() => {
  
    return delayedOrders.filter(o => {
  
      const matchSearch = [
        o.caseID,
        o.orderID,
        o.status]
        .filter(Boolean)
        .some(field =>
          field.toString().toLowerCase().includes(searchText.toLowerCase())
        );
  return matchSearch;
    })
  
  }, [delayedOrders, searchText])

  return (
<div className="container-fluid mt-4">



{/* KPI */}
  <div className="stats-grid mb-5">


 
    <div className="stat-box2">
      <h2 className="stat-number">{animatedKpi.totalCases}</h2>
      <div className="stat-label blue">
        <i className="bi bi-collection-fill"></i> Total
      </div>
    </div>



    <div className="stat-box2">
      <h2 className="stat-number">{animatedKpi.completedCases}</h2>
      <div className="stat-label green">
        <i className="bi bi-check-circle-fill"></i> Completed
      </div>
    </div>



    <div className="stat-box2">
      <h2 className="stat-number">{animatedKpi.millingQueueCount}</h2>
      <div className="stat-label yellow">
        <i className="bi bi-hourglass-split"></i> Queue
      </div>
    </div>


    <div className="stat-box2">
      <h2 className="stat-number">{animatedKpi.needsAttention}</h2>
      <div className="stat-label red">
        <i className="bi bi-exclamation-triangle-fill"></i> Attention
      </div>
    </div>


</div>

{/* HUB */}
<div className="premium-card mb-4 mt-3">

  <div className="section-title blacktext">Milling Hub Analysis</div>

  <div className="row g-4 mt-2">

   <div className="col-md-6 col-lg-3 col-12">
      <div className="hub-card hubcard-fix-height waiting">
        <div className="hub-icon"><i className="bi bi-hourglass-split"></i></div>
        <div>
          <span className="waiting-textjob">Waiting Jobs</span>
          <h4>{waitingJobs}</h4>
          <em>&nbsp;</em>
        </div>
      </div>
    </div>

     <div className="col-md-6 col-lg-3 col-12">
      <div className="hub-card best hubcard-fix-height">
        <div className="hub-icon"><i className="bi bi-trophy"></i></div>
        <div>
          <span>Best Performing Hub</span>
          <h6>{bestHub}</h6>
          <em className="green">Lowest queue load</em>
        </div>
      </div>
    </div>

   <div className="col-md-6 col-lg-3 col-12">
      <div className="hub-card overload hubcard-fix-height">
        <div className="hub-icon"><i className="bi bi-exclamation-triangle"></i></div>
        <div>
          <span>Overloaded</span>
          <h6>{worstHub}</h6>
          <em className="red">Highest  queue load</em>
        </div>
      </div>
    </div>
  <div className="col-md-6 col-lg-3 col-12">
      <div className="hub-card suggestion hubcard-fix-height">
        <div className="hub-icon"><i className="bi bi-lightbulb"></i></div>
        <div>
          <span>Suggestion</span>
          <p>{suggestion}</p>
        </div>
      </div>
    </div>
   

  </div>

 


{/*hub load section */}

{/* HUB LOAD SECTION */}
<div className="premium-card mt-4">

  <div className="section-title blacktext">Hub Load Distribution</div>

  <div className="row g-3 mt-2">

    {Object.entries(hubLoad).length === 0 ? (
      <div className="text-muted text-center p-3">
        No hub load data
      </div>
    ) : (
      Object.entries(hubLoad).map(([hub, count], index) => {

        const values = Object.values(hubLoad);
        const maxLoad = values.length ? Math.max(...values) : 1;
        const percentage = (count / maxLoad) * 100;

        return (
          <div className="col-12 col-md-6 col-lg-4" key={index}>

            <div className="hub-load-card">

              {/* HEADER */}
              <div className="hub-load-header">
                <h6>{hub}</h6>
                <span>{count} Jobs</span>
              </div>

              {/* PROGRESS BAR */}
              <div className="hub-progress">
                <div
                  className="hub-progress-bar"
                  style={{
                    width: `${percentage}%`
                  }}
                ></div>
              </div>

            </div>

          </div>
        );
      })
    )}

  </div>

</div>
{/*Hub Load section close */}


</div>

{/* TABLE */}
<div className="row g-4">
<div className="col-md-4">
<div className="table-card">
<div className="table-header">Designer Queue</div>
<DataTable columns={queueColumns} data={filterData(designerQueue)} pagination paginationComponentOptions={{
    noRowsPerPage: true, 
    rangeSeparatorText: "" 
  }}  fixedHeader/>
</div>
</div>

<div className="col-md-4">
<div className="table-card">
<div className="table-header">Milling Queue</div>
<DataTable columns={millingColumns} data={filterData(millingQueue)} pagination  paginationComponentOptions={{
    noRowsPerPage: true, 
    rangeSeparatorText: "" 
  }} fixedHeader/>
</div>
</div>

<div className="col-md-4">
<div className="table-card">
<div className="table-header">Delivery Queue</div>
<DataTable columns={queueColumns} data={filterData(deliveryQueue)} paginationComponentOptions={{
    noRowsPerPage: true, 
    rangeSeparatorText: "" 
  }} pagination fixedHeader/>
</div>
</div>
</div>

{/* CHART */}
<div className="row g-4 mt-4">

  {/* BAR CHART */}
  <div className="col-md-6">
  <div className="premium-card">

    <div className="chart-title">Hub Load Analysis</div>

  <ResponsiveContainer width="100%" height={300}>
  <BarChart data={hubPerformance}>
    
    <XAxis dataKey="hub" />
    <YAxis />

    <Bar
      dataKey="orders"
      radius={[8, 8, 0, 0]}
      animationDuration={800}
      maxBarSize={35}
      onClick={(data) => {
        setSelectedHub(data);
        setShowModal(true);
      }}
    >
      {hubPerformance.map((entry, index) => (
        <Cell
          key={`cell-${index}`}
          fill={getBarColor(entry.hub)}
        />
      ))}
    </Bar>

  </BarChart>
</ResponsiveContainer>

  </div>
</div>

  {/* LINE CHART */}
  <div className="col-md-6">
    <div className="premium-card">

      {/* ✅ HEADING */}
      <div className="chart-title">Hourly Production Trend</div>

      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={productionData}>
          <XAxis dataKey="hour" />
          <YAxis />
          <Tooltip />
          <Line dataKey="orders" stroke="#16a34a" />
        </LineChart>
      </ResponsiveContainer>

    </div>
  </div>

</div>


{/* DELAYED ORDERS */}
<div className="premium-card mt-4">

  <div className="section-title blacktext">Delayed Orders</div>
<div className="col-md-6 col-12 col-lg-3">
          <div className="input-group">
            <input
              type="text"
              className="form-control cancel-order-searchbtn"
              placeholder="Search order..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />

            <button className="btn btn-primary" type="button">
              <i className="bi bi-search"></i>
            </button>
          </div>
        </div>
  <div className="row g-3 mt-2">

    <DataTable
          columns={delayOrderColumns}
          data={filteredOrders}
          pagination
          highlightOnHover
          striped
          responsive
          progressPending={loading}
          fixedHeader
          fixedHeaderScrollHeight="500px"
        />

  </div>

</div>

</div>
  );
};

export default ManagerMillingPerformance;